import { Link } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import { VPD_PHASE_TARGETS } from "../utility/vpd.js";

const PHASES = [
  {
    name: "Inoculation",
    color: "bg-gray-400",
    days: "Day 0",
    summary: "Spawn or spores are introduced to the sterile substrate.",
    details:
      "The grow begins when living mycelium (grain spawn, liquid culture, or spores) is mixed into or placed on a prepared substrate. The environment should be as sterile as possible. Temperature is the main variable to dial in — most species colonize fastest in the 70–75°F range. Humidity and CO2 are secondary concerns at this stage since the substrate is sealed.",
    targets: { temp: "70–75°F", rh: "Sealed (not relevant)", co2: "Elevated OK", vpd: "Not enforced" },
  },
  {
    name: "Colonization",
    color: "bg-amber-500",
    days: "Days 1–14 (species-dependent)",
    summary: "Mycelium spreads through the substrate, consuming nutrients.",
    details:
      "White mycelium threads (hyphae) colonize the substrate. The block or bag is typically sealed — CO2 build-up is tolerated and even beneficial because it suppresses competing organisms. You want consistent temperature and minimal disturbance. Colonization is complete when the substrate is fully white with no visible uncolonized patches.",
    targets: { temp: "70–75°F", rh: "Sealed", co2: "Elevated OK (2000–5000 ppm)", vpd: "Not enforced" },
  },
  {
    name: "Pinning",
    color: "bg-orange-500",
    days: "Days 14–18",
    summary: "Environmental triggers cause the first primordia (baby mushrooms) to form.",
    details:
      "Pinning is initiated by a combination of signals: a drop in CO2 (fresh air exchange), introduction of light, and a slight drop in temperature. The mycelium interprets these as \"it is time to fruit.\" VPD becomes critical here — moderate evaporation (0.4–0.8 kPa) stresses the surface just enough to trigger pin formation. Too wet suppresses pins; too dry aborts them.",
    targets: { temp: "65–70°F", rh: "85–92%", co2: "< 1000 ppm", vpd: "0.4–0.8 kPa (target)" },
  },
  {
    name: "Fruiting",
    color: "bg-emerald-500",
    days: "Days 18–25",
    summary: "Pins develop into full mushrooms ready to harvest.",
    details:
      "Mushrooms grow rapidly once pins form. The goal is gentle, consistent evaporation — enough to keep caps from cracking or drying out, but not so humid that blotch bacteria proliferate. VPD targets tighten to 0.2–0.5 kPa. Misting frequency and fan speed are the main dials. CO2 should stay low to prevent long, spindly stems and underdeveloped caps.",
    targets: { temp: "65–70°F", rh: "88–95%", co2: "< 800 ppm", vpd: "0.2–0.5 kPa (target)" },
  },
  {
    name: "Harvesting",
    color: "bg-sky-500",
    days: "Days 22–28+",
    summary: "Mushrooms are picked at peak maturity; the block rests before the next flush.",
    details:
      "Harvest timing depends on species and preference — most are best just before or as the veil tears (if present). After harvest, the block surface is cleaned and allowed to rest for 5–7 days with higher humidity to encourage the next flush. Yield typically decreases with each flush as the substrate's nutrition is depleted.",
    targets: { temp: "Flexible", rh: "90%+ (rest period)", co2: "< 1000 ppm", vpd: "Flexible" },
  },
];

function TargetTable({ targets }) {
  return (
    <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
      {Object.entries(targets).map(([key, val]) => (
        <div key={key}>
          <dt className="text-gray-400 dark:text-gray-500 capitalize">{key === "rh" ? "Humidity" : key === "co2" ? "CO₂" : key === "vpd" ? "VPD" : key}</dt>
          <dd className="font-medium text-gray-700 dark:text-gray-200 mt-0.5">{val}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="text-lg font-semibold text-sky-800 dark:text-sky-300 mb-4 border-b border-gray-100 dark:border-gray-700 pb-2">{title}</h2>
      {children}
    </section>
  );
}

export default function Glossary() {
  return (
    <LayoutWide title="Glossary">
      <div className="flex items-center gap-2 mb-6 text-sm text-gray-400 dark:text-gray-500">
        <Link to="/grows" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Grows</Link>
        <span>/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">Glossary</span>
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-sky-800 dark:text-sky-300">Glossary</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          Reference guide for grow phases, environmental metrics, and how Fungistics calculates them.
        </p>
      </div>

      {/* Jump links */}
      <nav className="flex flex-wrap gap-2 mb-8 text-sm">
        {["grow-phases", "vpd", "stability-score", "sensors", "flush-cycles", "substrate", "contamination"].map((id) => (
          <a
            key={id}
            href={`#${id}`}
            className="px-3 py-1 rounded-full bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-400 border border-sky-200 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900 transition-colors capitalize"
          >
            {id.replace(/-/g, " ")}
          </a>
        ))}
      </nav>

      <div className="flex flex-col gap-10">

        {/* Grow Phases */}
        <Section id="grow-phases" title="Grow Phases">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
            Every grow in Fungistics moves through five sequential phases. Each phase has distinct environmental targets — the system tracks your sensors against these to compute stability scores and surface the phase bar on grow cards.
          </p>
          <div className="flex flex-col gap-4">
            {PHASES.map((phase) => (
              <div key={phase.name} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`w-3 h-3 rounded-sm shrink-0 ${phase.color}`} />
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100">{phase.name}</h3>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{phase.days}</span>
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{phase.summary}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{phase.details}</p>
                <TargetTable targets={phase.targets} />
              </div>
            ))}
          </div>
        </Section>

        {/* VPD */}
        <Section id="vpd" title="VPD — Vapor Pressure Deficit">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 flex flex-col gap-5">
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">What is VPD?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                VPD measures the "drying power" of air — the difference between how much moisture the air could hold at saturation and how much it actually holds. High VPD means dry, thirsty air that pulls moisture from surfaces quickly. Low VPD means air nearly saturated with water, so evaporation slows almost to a stop.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Why does it matter for mushrooms?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Mushrooms are 85–95% water. They lose moisture to the surrounding air continuously — the rate is governed by VPD. Too much evaporation (high VPD) causes cracking, early veil tearing, and aborted pins. Too little (low VPD) creates a stagnant surface layer that invites bacterial blotch and slows pin formation. Each phase has a target band where evaporation is just right.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">How Fungistics calculates it</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2">
                VPD is derived entirely from your existing temperature and humidity sensors — no additional hardware needed. The formula:
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-xs font-mono text-gray-700 dark:text-gray-300 leading-relaxed">
                <div>tC = (tempF − 32) × 5/9</div>
                <div>es = 0.6108 × exp(17.27 × tC / (tC + 237.3))  <span className="text-gray-400 dark:text-gray-500">// saturation vapor pressure (kPa)</span></div>
                <div>VPD = es × (1 − rh / 100)</div>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Based on the Tetens formula for saturation vapor pressure. Outputs kPa (kilopascals).
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Color scale</h3>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-blue-500 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-24">&lt; 0.1 kPa</span>
                  <span className="text-gray-500 dark:text-gray-400">Saturated — evaporation nearly stopped, bacterial blotch risk</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-green-500 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-24">0.1–0.5 kPa</span>
                  <span className="text-gray-500 dark:text-gray-400">Ideal for fruiting — gentle, consistent evaporation</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-yellow-400 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-24">0.5–0.9 kPa</span>
                  <span className="text-gray-500 dark:text-gray-400">Drying — monitor closely, mist if caps look dry</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-red-500 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-24">&gt; 0.9 kPa</span>
                  <span className="text-gray-500 dark:text-gray-400">Dangerous — cracking and abort risk, intervene immediately</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Phase targets</h3>
              <div className="flex flex-col gap-1.5 text-sm">
                {PHASES.map((p) => {
                  const target = VPD_PHASE_TARGETS[p.name];
                  return (
                    <div key={p.name} className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${p.color}`} />
                      <span className="w-28 font-medium text-gray-700 dark:text-gray-200">{p.name}</span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {target ? `${target[0]}–${target[1]} kPa` : "Not enforced"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        {/* Stability Score */}
        <Section id="stability-score" title="Environmental Stability Score">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 flex flex-col gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              The stability score (0–100) summarizes how consistently your environment has stayed within the current phase's VPD target band over the last 24 hours. A score of 100 means VPD never deviated from the target. A score of 0 means the average deviation was 0.5 kPa or more.
            </p>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">How it's calculated</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2">
                For each sensor reading in the last 24h, we compute how far the VPD was outside the target band (0 if inside). We average those deviations and map to a 0–100 scale:
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-xs font-mono text-gray-700 dark:text-gray-300 leading-relaxed">
                <div>deviation = max(0, lo − vpd) + max(0, vpd − hi)</div>
                <div>meanDev = average(deviations over last 24h)</div>
                <div>score = max(0, round(100 × (1 − meanDev / 0.5)))</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Score ranges</h3>
              <div className="flex flex-col gap-1.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-green-500 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-16">80–100</span>
                  <span className="text-gray-500 dark:text-gray-400">Excellent — environment tightly controlled</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-yellow-400 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-16">60–79</span>
                  <span className="text-gray-500 dark:text-gray-400">Good — occasional drift, worth monitoring</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-orange-400 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-16">40–59</span>
                  <span className="text-gray-500 dark:text-gray-400">Fair — consistent variance, check equipment</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-red-500 shrink-0" />
                  <span className="font-medium text-gray-700 dark:text-gray-200 w-16">0–39</span>
                  <span className="text-gray-500 dark:text-gray-400">Poor — significant deviation, intervention needed</span>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500">
              The score is only shown during Pinning and Fruiting phases, where VPD targets are defined. During Inoculation and Colonization the environment is usually sealed and VPD is not the primary concern.
            </p>
          </div>
        </Section>

        {/* Sensors */}
        <Section id="sensors" title="Sensors">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700">
            {[
              {
                name: "Temperature",
                unit: "°F",
                hardware: "DHT22, DS18B20",
                desc: "Air temperature inside the grow chamber. Most mushroom species fruit best in the 60–72°F range. Higher temps accelerate growth but also favor competing organisms and reduce quality. Temperature is also the primary input to VPD calculation.",
                range: "60–75°F typical",
              },
              {
                name: "Humidity (RH)",
                unit: "%",
                hardware: "DHT22",
                desc: "Relative humidity — the percentage of water vapor in the air relative to saturation at the current temperature. High RH keeps surfaces moist but does not directly control evaporation rate; VPD (which combines temp and RH) is a more precise guide.",
                range: "80–95% typical",
              },
              {
                name: "CO₂",
                unit: "ppm",
                hardware: "SCD4x",
                desc: "Carbon dioxide concentration in parts per million. Mycelium produces CO2 during colonization. High CO2 during fruiting causes long, spindly stems and small, poorly-formed caps (a condition called 'morphological abnormality'). Fresh air exchange (FAE) is the primary lever for CO2 control.",
                range: "Colonization: 2000–5000 ppm, Fruiting: < 800 ppm",
              },
              {
                name: "VPD",
                unit: "kPa",
                hardware: "Derived (no extra sensor)",
                desc: "Computed from temperature and humidity. See the VPD section above for full explanation.",
                range: "0.2–0.8 kPa target depending on phase",
              },
            ].map((sensor) => (
              <div key={sensor.name} className="p-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100">{sensor.name}</h3>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{sensor.unit} — {sensor.hardware}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-1">{sensor.desc}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Typical range: {sensor.range}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Flush Cycles */}
        <Section id="flush-cycles" title="Flush Cycles">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              A flush is one wave of mushroom production from a substrate block. After harvesting, the block rests for 5–10 days — the surface dries slightly, then rehydrates — triggering a second flush of pins. Most substrate types support 2–4 flushes before the nutrition is exhausted.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              Yields typically follow a declining pattern: the first flush is usually the largest (40–60% of total yield), the second is moderate, and subsequent flushes produce diminishing returns. Some cultivators dunk or soak their blocks in cold water between flushes to rehydrate the substrate and stimulate pinning.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Fungistics tracks each harvest by flush number so you can compare yield across flushes and decide when a block is spent.
            </p>
          </div>
        </Section>

        {/* Substrate */}
        <Section id="substrate" title="Substrate">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              Substrate is the growing medium — the nutrient-rich material that mycelium colonizes and consumes to produce mushrooms. Common substrates include:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 mb-3">
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Hardwood sawdust + bran</span> — widely used for oysters, lion's mane, shiitake. High yield, sterile preparation required.</li>
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Straw</span> — popular for oyster mushrooms, pasteurization (not full sterilization) is sufficient. Lower yield but lower cost.</li>
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Manure-based (casing)</span> — used for portobello and cremini. Requires a casing layer after colonization.</li>
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Grain (rye, wheat, oats)</span> — used as spawn to inoculate bulk substrate, not typically a standalone fruiting medium.</li>
            </ul>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              The substrate you use affects colonization speed, flush count, and susceptibility to contamination. Logging substrate type in your grow lets you compare outcomes across batches.
            </p>
          </div>
        </Section>

        {/* Contamination */}
        <Section id="contamination" title="Contamination">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
              Contamination occurs when organisms other than your target mushroom species establish in the substrate or grow environment. The most common types:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 mb-3">
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Green/black mold (Trichoderma, Aspergillus)</span> — the most common. Spreads rapidly, competes directly with mycelium for nutrients, and can produce toxins. A contaminated block should be removed from the grow space immediately in a sealed bag.</li>
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Bacterial blotch</span> — slimy, discolored patches on caps or substrate, typically caused by Pseudomonas. Often a result of excess surface moisture (low VPD). Can sometimes be managed by increasing airflow and reducing misting.</li>
              <li><span className="font-medium text-gray-700 dark:text-gray-200">Wet rot / sour rot</span> — foul smell, mushy substrate. Usually due to improper sterilization or contaminated grain spawn.</li>
            </ul>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Use the "contam spotted" event type in grow notes to log when contamination first appears — this creates a timestamped annotation on your sensor charts so you can correlate environmental conditions with contamination events across grows.
            </p>
          </div>
        </Section>

      </div>
    </LayoutWide>
  );
}
